/**
 * Cycle 53 — Hidden version sentinel.
 *
 * Renders the server-reported ``BUILD_VERSION`` into a small,
 * unobtrusive footer pill (bottom-right). Useful for two things:
 *
 *   1. Ops / users can confirm at a glance which build their browser
 *      is actually showing — if the pill says ``unknown`` or an
 *      older version than expected, they know they're on a stale
 *      bundle and a hard refresh is warranted.
 *   2. Devtools-friendly: the ``data-build-version`` attribute is
 *      stable and machine-readable so future Playwright/Cypress
 *      tests can assert deploy parity.
 *
 * Polls ``/api/system/version`` once on mount (the ``useVersionCheck``
 * hook handles ongoing change detection — this component is just for
 * surface-level visibility).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { BUNDLE_CYCLE } from "../buildInfo";

const API = process.env.REACT_APP_BACKEND_URL;


export function VersionSentinel() {
  const [v, setV] = useState({ version: "?", built_at: null });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/system/version`, { headers: { "Cache-Control": "no-cache" } })
      .then((r) => {
        if (!cancelled && r.data) {
          setV({
            version: r.data.version || "?",
            built_at: r.data.built_at || null,
          });
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="fixed bottom-1 right-2 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-400 dark:text-gray-600 bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm border border-gray-200/40 dark:border-gray-700/40 pointer-events-none select-none z-50"
      title={`Bundle ${BUNDLE_CYCLE}${v.built_at ? ` · Server ${v.version} · ${v.built_at}` : ` · Server ${v.version}`}`}
      data-testid="version-sentinel"
      data-build-version={v.version}
      data-bundle-cycle={BUNDLE_CYCLE}
    >
      v.{v.version}
    </div>
  );
}

export default VersionSentinel;
